import React, { useState, useEffect, useRef, useMemo, useCallback, memo } from "react";
import { NodeResizer, useReactFlow } from "reactflow";
import ReactQuill, { Quill } from "react-quill";
import "react-quill/dist/quill.snow.css";
import { FaArrowRotateRight } from "react-icons/fa6";

// 커스텀 글씨 크기 블로터 등록 (style attributor 사용)
const SizeStyle = Quill.import('attributors/style/size');
SizeStyle.whitelist = ['8px', '9px', '10px', '11px', '12px', '14px', '16px', '18px', '24px', '36px', '48px', '72px', '96px'];
Quill.register(SizeStyle, true);


const TextBoxNode = memo(function TextBoxNode({ data, selected, width: nodeWidth, height: nodeHeight, id }) {
  // 노드의 width/height 속성을 우선 사용, 없으면 data에서 가져옴
  const width = nodeWidth || data?.width || 100;
  const height = nodeHeight || data?.height || 100;
  const {
    text = '<p class="ql-align-center" style="font-size: 16px;">텍스트 입력</p>',
    fontSize = "16px",
    rotation = 0,
    fill = "transparent",
    stroke = "#000000",
    strokeWidth = 0,
    fillOpacity = 1,
    strokeOpacity = 1,
    fillRgba,
    strokeRgba
  } = data || {};
  const [isEditing, setIsEditing] = useState(data?.isEditing || false);
  const [inputText, setInputText] = useState(text);
  const wrapperRef = useRef(null);
  const displayRef = useRef(null);
  const nodeRef = useRef(null);
  const { setNodes } = useReactFlow();

  const [isShiftPressed, setIsShiftPressed] = useState(false);

  useEffect(() => {
    if (!selected) return;
    const onKey = (e) => { if (e.key === 'Shift') setIsShiftPressed(e.type === 'keydown'); };
    window.addEventListener('keydown', onKey);
    window.addEventListener('keyup', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('keyup', onKey);
      setIsShiftPressed(false);
    };
  }, [selected]);

  // RGBA 색상이 있으면 사용, 없으면 opacity로 변환
  const fillColor = fillRgba || (fill === 'transparent' ? 'transparent' : fillOpacity < 1
    ? (() => {
      const r = parseInt(fill.slice(1, 3), 16);
      const g = parseInt(fill.slice(3, 5), 16);
      const b = parseInt(fill.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${fillOpacity})`;
    })()
    : fill);

  const strokeColor = strokeRgba || (strokeOpacity < 1
    ? (() => {
      const r = parseInt(stroke.slice(1, 3), 16);
      const g = parseInt(stroke.slice(3, 5), 16);
      const b = parseInt(stroke.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${strokeOpacity})`;
    })()
    : stroke);

  // 편집 상태 동기화
  useEffect(() => {
    const currentUser = data?.currentUser;
    const editingBy = data?.editingBy;
    const dataIsEditing = data?.isEditing;

    // 다른 사용자가 편집을 시작한 경우 -> 내 편집 모드 강제 종료
    if (dataIsEditing && editingBy && editingBy !== currentUser && isEditing) {
      console.log('⚠️ 다른 사용자가 편집을 시작하여 편집 모드 종료:', editingBy);
      setIsEditing(false);
      return;
    }

    // 내가 편집을 시작해야 하는 경우 (초기 생성 시 등)
    if (dataIsEditing && !editingBy && !isEditing) {
      setIsEditing(true);
      // isEditing 플래그 제거 (한 번만 실행되도록)
      setNodes((nds) =>
        nds.map((node) =>
          node.id === id
            ? {
              ...node,
              data: {
                ...node.data,
                isEditing: false
              }
            }
            : node
        )
      );
    }
  }, [data?.isEditing, data?.editingBy, data?.currentUser, isEditing, id, setNodes]);

  useEffect(() => {
    if (!isEditing) {
      setInputText(text);
    }
  }, [text, isEditing]);



  // 편집 모드 진입 시 ReactQuill 포커스 및 이전 폰트 크기 유지
  useEffect(() => {
    if (isEditing && wrapperRef.current) {
      const timer = setTimeout(() => {
        const editorElement = wrapperRef.current?.querySelector('.ql-editor');
        const quill = wrapperRef.current?.querySelector('.ql-container')?.__quill;

        if (quill && editorElement) {
          const length = quill.getLength();

          // 전체 텍스트 선택하여 포맷 확인
          quill.setSelection(0, length, 'silent');
          const currentFormats = quill.getFormat();

          console.log('현재 포맷:', currentFormats, 'length:', length);

          // 기존 폰트 크기 추출 (HTML에서)
          let existingFontSize = '16px'; // 기본값

          // inputText에서 font-size 추출
          if (inputText) {
            const fontSizeMatch = inputText.match(/font-size:\s*(\d+px)/);
            if (fontSizeMatch) {
              existingFontSize = fontSizeMatch[1];
            } else if (currentFormats.size) {
              existingFontSize = currentFormats.size;
            }
          }

          console.log('적용할 폰트 크기:', existingFontSize);

          // 이전 폰트 크기 또는 기본값(16px) 적용
          if (length > 1) {
            // 기존 폰트 크기가 없는 부분에만 적용
            if (!currentFormats.size) {
              quill.formatText(0, length, 'size', existingFontSize, 'silent');
            }
            // 정렬은 기존 값 유지 (강제 설정하지 않음)
          } else {
            // 빈 에디터일 때만 기본값 설정
            quill.format('size', existingFontSize, 'silent');
            quill.format('align', 'center', 'silent');
          }

          // 커서를 끝으로 이동
          quill.setSelection(length, 0, 'silent');

          // 포커스 설정
          editorElement.focus();
        }
      }, 200);

      return () => clearTimeout(timer);
    }
  }, [isEditing, fontSize, inputText]);

  // 외부 클릭 감지 (ReactQuill 툴바 클릭은 제외)
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isEditing && wrapperRef.current) {
        const target = event.target;

        // ReactQuill 관련 요소인지 확인
        const isQuillElement = target.closest('.ql-toolbar') ||
          target.closest('.ql-editor') ||
          target.closest('.ql-container') ||
          target.closest('.ql-picker') ||
          target.closest('.ql-picker-options') ||
          target.classList.contains('ql-toolbar') ||
          target.classList.contains('ql-editor') ||
          target.classList.contains('ql-container') ||
          target.classList.contains('ql-picker') ||
          target.classList.contains('ql-picker-options');

        const isInsideWrapper = wrapperRef.current.contains(target);

        // ReactQuill 요소가 아니고 wrapper 외부를 클릭한 경우에만 편집 종료
        if (!isQuillElement && !isInsideWrapper) {
          console.log('📝 TextBox 편집 종료 (외부 클릭):', { textLength: inputText?.length });
          setIsEditing(false);
          if (data?.onUpdateText) {
            data.onUpdateText(inputText);
          }
          // 부모 컴포넌트에 편집 상태 알림
          if (data?.onUpdateEditingState) {
            data.onUpdateEditingState(false);
          }
        }
      }
    };

    if (isEditing) {
      // 즉시 이벤트 리스너 등록 (다음 프레임에서 등록하여 현재 클릭 이벤트는 제외)
      const timeoutId = setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside, true);
      }, 10);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener('mousedown', handleClickOutside, true);
      };
    }
  }, [isEditing, inputText, data]);

  // ReactQuill 툴바 설정
  const modules = useMemo(() => ({
    toolbar: {
      container: [
        [{ 'font': [] }],
        [{ 'size': SizeStyle.whitelist }],
        ['bold', 'italic', 'underline', 'strike'],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['clean']
      ],
    },
  }), []);

  const formats = [
    'font', 'size',
    'bold', 'italic', 'underline', 'strike',
    'color', 'background',
    'align'
  ];

  const handleDoubleClick = () => {
    console.log('-----------------------------handleDoubleClick--------------------------------');

    setIsEditing(true);
    // 부모 컴포넌트에 편집 상태 알림
    if (data?.onUpdateEditingState) {
      data.onUpdateEditingState(true);
    }
  };


  // ⚡ 실시간 텍스트 동기화 throttle
  const textSyncThrottleRef = useRef(null);
  const TEXT_SYNC_THROTTLE_MS = 200; // 200ms 간격으로 동기화

  const handleChange = (content) => {
    setInputText(content);

    // ⚡ 실시간 텍스트 동기화 (throttle)
    if (data?.onTextChangeLive) {
      if (!textSyncThrottleRef.current) {
        textSyncThrottleRef.current = setTimeout(() => {
          textSyncThrottleRef.current = null;
        }, TEXT_SYNC_THROTTLE_MS);
        data.onTextChangeLive(content);
      }
    }
  };

  // 컴포넌트 언마운트 시 throttle 타이머 정리
  useEffect(() => {
    return () => {
      if (textSyncThrottleRef.current) {
        clearTimeout(textSyncThrottleRef.current);
      }
    };
  }, []);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      console.log('📝 TextBox 편집 종료 (ESC 키):', { textLength: inputText?.length });
      // Escape 키로 편집 종료 시에도 fontSize를 적용한 HTML로 저장
      if (data?.onUpdateText) {
        data.onUpdateText(inputText);
      }
      setIsEditing(false);
      // 부모 컴포넌트에 편집 상태 알림
      if (data?.onUpdateEditingState) {
        data.onUpdateEditingState(false);
      }
    }
  }, [data, inputText]);

  // HTML에서 모든 font-size 관련 스타일을 제거하고 fontSize로 교체 (메모이제이션)
  const processedHtml = useMemo(() => {
    if (!text) return '<p class="ql-align-center" style="font-size: 16px;">텍스트 입력</p>';
    return text;
  }, [text]);


  return (
    <>
      <div
        ref={nodeRef}
        id={`node-${id}`}
        style={{
          width,
          height,
          position: 'relative',
          transform: `rotate(${rotation}deg)`,
          transformOrigin: 'center center'
        }}
      >

        <NodeResizer
          color="#000000"
          lineColor="#000000"
          lineStyle={{ stroke: "#000000", strokeWidth: 1 }}
          handleStyle={{
            backgroundColor: "#ffffff",
            border: "4px solid #000000",
            borderRadius: "50%",
            width: "20px",
            height: "20px",
            boxSizing: "border-box",
            boxSizing: "border-box",
          }}
          isVisible={selected}
          minWidth={30}
          minHeight={50}
          keepAspectRatio={isShiftPressed}
        />

        {/* 다른 사용자가 편집 중일 때 표시 */}
        {data?.isEditing && data?.editingBy && data?.editingBy !== data?.currentUser && (
          <div style={{
            position: 'absolute',
            top: -25,
            left: 0,
            background: 'rgba(255, 152, 0, 0.9)',
            color: 'white',
            padding: '2px 8px',
            borderRadius: '3px',
            fontSize: '12px',
            fontWeight: 'bold',
            zIndex: 1000,
            whiteSpace: 'nowrap'
          }}>
            🔒 {data.editingBy} 편집 중
          </div>
        )}

        {/* 콘텐츠 */}
        <div
          onDoubleClick={handleDoubleClick}
          style={{
            width: '100%',
            height: '100%',
            background: fillColor,
            border: data?.isEditing && data?.editingBy && data?.editingBy !== data?.currentUser
              ? '3px solid rgba(255, 152, 0, 0.8)' // 다른 사용자가 편집 중일 때 주황색 테두리
              : `${strokeWidth}px solid ${strokeColor}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 8,
            boxSizing: 'border-box',
            cursor: isEditing ? 'text' : 'pointer',
            pointerEvents: data?.isEditing && data?.editingBy && data?.editingBy !== data?.currentUser
              ? 'none' // 다른 사용자가 편집 중일 때 클릭 방지
              : 'auto'
          }}
        >
          {isEditing ? (
            <div
              ref={wrapperRef}
              style={{
                width: '100%',
                height: '100%',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
              }}
              onKeyDown={handleKeyDown}
              tabIndex={-1}
            >
              <ReactQuill
                theme="snow"
                value={inputText}
                onChange={handleChange}
                modules={modules}
                formats={formats}
                style={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
                bounds="self"
              />
              <style>{`
              .ql-container {
                border: none !important;
                flex: 1;
                display: flex;
                flex-direction: column;
              }
              .ql-editor {
                flex: 1;
                padding: 0 !important;
                margin: 0 !important;
                overflow: auto;
                font-size: 16px !important;
                white-space: pre-wrap !important;
                word-wrap: break-word !important;
                overflow-wrap: break-word !important;
                display: flex !important;
                flex-direction: column !important;
                justify-content: center !important;
              }
              .ql-editor * {
                margin: 0 !important;
              }
              .ql-editor *:not([style*="font-size"]) {
                font-size: ${fontSize};
              }
              .ql-editor .ql-align-center {
                text-align: center !important;
              }
              .ql-editor .ql-align-right {
                text-align: right !important;
              }
              .ql-editor .ql-align-left {
                text-align: left !important;
              }
              .ql-editor .ql-align-justify {
                text-align: justify !important;
              }
              .ql-toolbar {
                position: absolute !important;
                top: -55px !important;
                left: -10px !important;
                transform: scale(${Math.max(1, Math.min(width / 500, 2.5))}) !important;
                transform-origin: left bottom !important;
                min-width: 500px !important;
                width: max-content !important;
                border: none !important;
                padding: 4px !important;
                background: rgba(255, 255, 255, 0.95) !important;
                border-radius: 4px !important;
                box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
                z-index: 1000 !important;
                margin-bottom: 0 !important;
              }
              .ql-toolbar .ql-formats {
                margin-right: 8px;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="8px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="8px"]::before {
                content: '8px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="9px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="9px"]::before {
                content: '9px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="10px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="10px"]::before {
                content: '10px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="11px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="11px"]::before {
                content: '11px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="12px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="12px"]::before {
                content: '12px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="14px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="14px"]::before {
                content: '14px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="16px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="16px"]::before {
                content: '16px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="18px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="18px"]::before {
                content: '18px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="24px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="24px"]::before {
                content: '24px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="36px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="36px"]::before {
                content: '36px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="48px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="48px"]::before {
                content: '48px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="72px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="72px"]::before {
                content: '72px' !important;
              }
              .ql-picker.ql-size .ql-picker-item[data-value="96px"]::before,
              .ql-picker.ql-size .ql-picker-label[data-value="96px"]::before {
                content: '96px' !important;
              }
              .ql-picker.ql-size .ql-picker-label::after {
                display: none !important;
              }
              .ql-picker.ql-size .ql-picker-label.ql-active::after {
                display: none !important;
              }
            `}</style>
            </div>
          ) : (
            <>
              <style>{`
              .textbox-display-wrapper {
                font-size: ${fontSize};
                padding: 0 !important;
                margin: 0 !important;
                display: flex;
                flex-direction: column;
                justify-content: center;
                height: 100%;
              }
              .textbox-display-wrapper * {
                margin: 0 !important;
              }
              .textbox-display-wrapper p:not([class*="ql-align"]) {
                text-align: left !important;
              }
              .textbox-display-wrapper *:not([style*="font-size"]) {
                font-size: ${fontSize};
              }
              .textbox-display-wrapper [style*="font-size: 8px"],
              .textbox-display-wrapper [style*="font-size:8px"] {
                font-size: 8px !important;
              }
              .textbox-display-wrapper [style*="font-size: 9px"],
              .textbox-display-wrapper [style*="font-size:9px"] {
                font-size: 9px !important;
              }
              .textbox-display-wrapper [style*="font-size: 10px"],
              .textbox-display-wrapper [style*="font-size:10px"] {
                font-size: 10px !important;
              }
              .textbox-display-wrapper [style*="font-size: 11px"],
              .textbox-display-wrapper [style*="font-size:11px"] {
                font-size: 11px !important;
              }
              .textbox-display-wrapper [style*="font-size: 12px"],
              .textbox-display-wrapper [style*="font-size:12px"] {
                font-size: 12px !important;
              }
              .textbox-display-wrapper [style*="font-size: 14px"],
              .textbox-display-wrapper [style*="font-size:14px"] {
                font-size: 14px !important;
              }
              .textbox-display-wrapper [style*="font-size: 16px"],
              .textbox-display-wrapper [style*="font-size:16px"] {
                font-size: 16px !important;
              }
              .textbox-display-wrapper [style*="font-size: 18px"],
              .textbox-display-wrapper [style*="font-size:18px"] {
                font-size: 18px !important;
              }
              .textbox-display-wrapper [style*="font-size: 24px"],
              .textbox-display-wrapper [style*="font-size:24px"] {
                font-size: 24px !important;
              }
              .textbox-display-wrapper [style*="font-size: 36px"],
              .textbox-display-wrapper [style*="font-size:36px"] {
                font-size: 36px !important;
              }
              .textbox-display-wrapper [style*="font-size: 48px"],
              .textbox-display-wrapper [style*="font-size:48px"] {
                font-size: 48px !important;
              }
              .textbox-display-wrapper [style*="font-size: 72px"],
              .textbox-display-wrapper [style*="font-size:72px"] {
                font-size: 72px !important;
              }
              .textbox-display-wrapper [style*="font-size: 96px"],
              .textbox-display-wrapper [style*="font-size:96px"] {
                font-size: 96px !important;
              }
              .textbox-display-wrapper .ql-font-serif {
                font-family: Georgia, "Times New Roman", serif !important;
              }
              .textbox-display-wrapper .ql-font-monospace {
                font-family: Monaco, "Courier New", monospace !important;
              }
              .textbox-display-wrapper .ql-font-arial {
                font-family: Arial, Helvetica, sans-serif !important;
              }
              .textbox-display-wrapper .ql-font-comic-sans {
                font-family: "Comic Sans MS", cursive !important;
              }
              .textbox-display-wrapper .ql-font-courier-new {
                font-family: "Courier New", Courier, monospace !important;
              }
              .textbox-display-wrapper .ql-font-georgia {
                font-family: Georgia, serif !important;
              }
              .textbox-display-wrapper .ql-font-helvetica {
                font-family: Helvetica, Arial, sans-serif !important;
              }
              .textbox-display-wrapper .ql-font-lucida {
                font-family: "Lucida Console", Monaco, monospace !important;
              }
              .textbox-display-wrapper .ql-font-times-new-roman {
                font-family: "Times New Roman", Times, serif !important;
              }
              .textbox-display-wrapper .ql-font-verdana {
                font-family: Verdana, Geneva, sans-serif !important;
              }
              .textbox-display-wrapper .ql-align-center {
                text-align: center !important;
              }
              .textbox-display-wrapper .ql-align-right {
                text-align: right !important;
              }
              .textbox-display-wrapper .ql-align-left {
                text-align: left !important;
              }
              .textbox-display-wrapper .ql-align-justify {
                text-align: justify !important;
              }
            `}</style>
              <div
                ref={displayRef}
                className="textbox-display-wrapper"
                style={{
                  color: '#374151',
                  wordBreak: 'break-word',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  whiteSpace: 'pre-wrap',
                  width: '100%',
                  maxWidth: '100%',
                  height: '100%',
                  padding: 0,
                  margin: 0,
                  overflow: 'auto',
                }}
                dangerouslySetInnerHTML={{ __html: processedHtml }}
              />
            </>
          )}
        </div>
      </div>

    </>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.id === nextProps.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.width === nextProps.width &&
    prevProps.height === nextProps.height &&
    prevProps.data?.width === nextProps.data?.width &&
    prevProps.data?.height === nextProps.data?.height &&
    prevProps.data?.text === nextProps.data?.text &&
    prevProps.data?.fontSize === nextProps.data?.fontSize &&
    prevProps.data?.rotation === nextProps.data?.rotation &&
    prevProps.data?.fill === nextProps.data?.fill &&
    prevProps.data?.stroke === nextProps.data?.stroke &&
    prevProps.data?.strokeWidth === nextProps.data?.strokeWidth &&
    prevProps.data?.fillOpacity === nextProps.data?.fillOpacity &&
    prevProps.data?.strokeOpacity === nextProps.data?.strokeOpacity &&
    prevProps.data?.fillRgba === nextProps.data?.fillRgba &&
    prevProps.data?.strokeRgba === nextProps.data?.strokeRgba &&
    prevProps.data?.isEditing === nextProps.data?.isEditing &&
    prevProps.data?.editingBy === nextProps.data?.editingBy &&
    prevProps.data?.currentUser === nextProps.data?.currentUser &&
    prevProps.data?.selectedBy === nextProps.data?.selectedBy &&
    prevProps.data?.showUserLabel === nextProps.data?.showUserLabel &&
    prevProps.data?.onUpdateText === nextProps.data?.onUpdateText &&
    prevProps.data?.onUpdateEditingState === nextProps.data?.onUpdateEditingState &&
    prevProps.data?.onTextChangeLive === nextProps.data?.onTextChangeLive
  );
});

export default TextBoxNode;

